/**
 * Resonance meter, multiplier, Overdrive and score (Design Bible §8). Grazing and beat-accurate
 * flying build Resonance; at 100 you enter Overdrive (10 s, score ×3, speed tier +1) and the meter
 * drains to 0. Safe-but-clean play lands near 40 % of expert graze-heavy play (Pillar P4) because
 * the multiplier and Overdrive — reached by grazing — carry most of the score.
 */
import {
  MULTIPLIER_MAX,
  MULTIPLIER_STEP_HAZARDS,
  OVERDRIVE_DURATION,
  OVERDRIVE_SCORE_MULT,
  RESONANCE_MAX,
  RES_BEAT_STEER,
  RES_GRAZE,
  RES_IDLE_AFTER_S,
  RES_IDLE_PER_S,
  RES_NODE_CENTRE,
  SCORE_DISTANCE_RATE,
} from '../core/constants';
import { clamp } from '../core/math-det';
import type { EventBus } from '../core/events';
import type { EventMap, ResonanceCause } from '../core/event-map';

const GRAZE_BONUS_BASE = 6; // FS-05 §5.3 — the P4 knob (small on purpose).

export class Resonance {
  value = 0;
  multiplier = 1;
  score = 0;
  overdrive = false;
  overdriveTimer = 0;
  peakResonance = 0;
  overdriveTime = 0;

  /** Zen (FS-09 R24): score is not computed. Set by `Game.beginRun` from the mode's rules. */
  scoreEnabled = true;
  /** Null hull (FS-10 §4.1): all score ×2, composed multiplicatively with Overdrive's ×3. */
  hullScoreMult = 1;

  /** Run-scoped stats read by `Game` when it builds the `RunSummary` for unlock evaluation. */
  multiplierPeak = 1;
  overdrivesThisRun = 0;
  beatSteersCount = 0;

  private consecutiveClears = 0;
  private timeSinceGraze = 0;

  constructor(private bus: EventBus<EventMap>) {}

  reset(): void {
    this.value = 0;
    this.multiplier = 1;
    this.score = 0;
    this.overdrive = false;
    this.overdriveTimer = 0;
    this.consecutiveClears = 0;
    this.timeSinceGraze = 0;
    this.peakResonance = 0;
    this.overdriveTime = 0;
    this.scoreEnabled = true;
    this.hullScoreMult = 1;
    this.multiplierPeak = 1;
    this.overdrivesThisRun = 0;
    this.beatSteersCount = 0;
  }

  private get scoreMult(): number {
    return this.multiplier * (this.overdrive ? OVERDRIVE_SCORE_MULT : 1) * this.hullScoreMult;
  }

  private add(delta: number, cause: ResonanceCause): void {
    if (delta === 0) return;
    const before = this.value;
    this.value = clamp(this.value + delta, 0, RESONANCE_MAX);
    if (this.value > this.peakResonance) this.peakResonance = this.value;
    if (this.value !== before) {
      this.bus.emit('resonance:changed', { value: this.value, delta: this.value - before, cause });
    }
    if (this.value >= RESONANCE_MAX && !this.overdrive) this.startOverdrive();
  }

  onGraze(): void {
    this.timeSinceGraze = 0;
    this.add(RES_GRAZE, 'graze');
    if (this.scoreEnabled) this.score += GRAZE_BONUS_BASE * this.scoreMult;
  }

  onNodeCentre(): void {
    this.add(RES_NODE_CENTRE, 'node');
  }

  onBeatSteer(bonusMult: number): void {
    this.beatSteersCount++;
    this.add(RES_BEAT_STEER * bonusMult, 'beat');
  }

  onHazardCleared(): void {
    this.consecutiveClears++;
    if (this.consecutiveClears % MULTIPLIER_STEP_HAZARDS === 0 && this.multiplier < MULTIPLIER_MAX) {
      this.multiplier++;
      if (this.multiplier > this.multiplierPeak) this.multiplierPeak = this.multiplier;
      this.bus.emit('multiplier:changed', { value: this.multiplier });
    }
  }

  fillToMax(): void {
    this.add(RESONANCE_MAX - this.value, 'overdrive-core');
  }

  onHit(): void {
    this.consecutiveClears = 0;
    if (this.multiplier !== 1) {
      this.multiplier = 1;
      this.bus.emit('multiplier:changed', { value: 1 });
    }
    const before = this.value;
    this.value = 0;
    if (before !== 0) this.bus.emit('resonance:changed', { value: 0, delta: -before, cause: 'hit' });
  }

  private startOverdrive(): void {
    this.overdrive = true;
    this.overdriveTimer = OVERDRIVE_DURATION;
    this.overdrivesThisRun++;
    this.bus.emit('overdrive:start', { simTick: 0 });
  }

  /** Advance one sim step. `distanceThisStep` = scrollSpeed * dt. */
  step(dt: number, distanceThisStep: number, intensity: number): void {
    // Score from distance travelled.
    if (this.scoreEnabled) this.score += distanceThisStep * SCORE_DISTANCE_RATE * intensity * this.scoreMult;

    if (this.overdrive) {
      this.overdriveTime += dt;
      this.overdriveTimer -= dt;
      // Drain to 0 across the 10 s.
      this.value = clamp((this.overdriveTimer / OVERDRIVE_DURATION) * RESONANCE_MAX, 0, RESONANCE_MAX);
      this.bus.emit('resonance:changed', { value: this.value, delta: 0, cause: 'overdrive-core' });
      if (this.overdriveTimer <= 0) {
        this.overdrive = false;
        this.value = 0;
        this.bus.emit('overdrive:end', { simTick: 0 });
      }
      return;
    }

    // Idle drain after 1 s without a graze.
    this.timeSinceGraze += dt;
    if (this.timeSinceGraze > RES_IDLE_AFTER_S && this.value > 0) {
      this.add(RES_IDLE_PER_S * dt, 'idle');
    }
  }
}
